import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Grid, Typography } from '@mui/material';
import { useFlag } from '@unleash/proxy-client-react';
import { useAtom } from 'jotai';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { hintsAtom } from '@/atoms/hints';
import { CloseSvg } from '@/components/Icons/Close.Svg';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { HINTS, type HintType } from '@/constants';
import type { hintsBlock } from '@/enums';
import { useColorMode } from '@/hooks';
import { HintBullet, HintContentWrap } from './Hint.styles';

export interface IHintContent {
    block: hintsBlock;
    hint: HintType;
    size?: 'large';
}

export const HintContent = memo(({ block, hint, size }: IHintContent) => {
    const isDarkMode = useColorMode();
    const { t } = useTranslation();
    const isPro = useFlag('pro');
    const [hintState, setHint] = useAtom(hintsAtom);

    const hintsBlock = HINTS[block] as Record<string, HintType>;
    const hintStack = Object.keys(hintsBlock).filter(hint => isPro || hintsBlock[hint]?.isPro === isPro);

    const firstHint = !hintStack.indexOf(hint.name);
    const lastHint = hintStack.indexOf(hint.name) + 1 === hintStack.length;

    const prevHint = () => {
        setHint(prev => ({
            ...prev,
            item: hintStack[hintStack.indexOf(hint.name) - 1] ?? null,
        }));
    };

    const nextHint = () => {
        setHint(prev => ({
            ...prev,
            item: hintStack[hintStack.indexOf(hint.name) + 1] ?? null,
        }));
    };

    const closeHint = () => {
        setHint({ block: null, item: null });
    };

    return (
        <HintContentWrap size={size}>
            <div className="flex flex-wrap w-full justify-between mb-[20px]">
                <Typography
                    variant="body2"
                    children={t('hints.block', { name: t(`hints.blocks.${block}.name`) })}
                    color={theme => (!isDarkMode ? theme.custom.c4 : theme.custom.c2)}
                    textTransform="uppercase"
                    lineHeight={1}
                />
                <Box display="flex" onClick={closeHint} sx={{ cursor: 'pointer' }}>
                    <CloseSvg sx={theme => ({ color: theme.custom.c1, fontSize: '0.875rem' })} />
                </Box>
            </div>
            <Grid container gap="6px" color="primary.main">
                <Text
                    variant="body1"
                    color="text-inherit"
                    children={t(`hints.blocks.${block}.items.${hint.name}.name`)}
                    className="text-[1.375rem] font-semibold leading-[1.3]"
                />
                <Text
                    variant="body1"
                    color="text-inherit"
                    children={t(`hints.blocks.${block}.items.${hint.name}.text`)}
                    className="leading-[1.3]"
                />
            </Grid>
            {Boolean(hintStack.length) && (
                <>
                    <div className="flex flex-wrap w-full h-[8px] gap-[14px] my-[24px] items-center">
                        {hintStack.map((hint, index) => (
                            <HintBullet key={`${index}-${hint}`} active={hint === hintState.item} />
                        ))}
                    </div>
                    <div className="flex flex-wrap w-full justify-between">
                        <div className="flex flex-wrap gap-[12px] w-auto">
                            <Button
                                variant="actions"
                                size="small"
                                onClick={prevHint}
                                disabled={firstHint}
                                children={<NavigateBeforeIcon />}
                            />
                            <Button variant="actions" size="small" onClick={nextHint} disabled={lastHint} children={<NavigateNextIcon />} />
                        </div>
                        {/* Future feature >> */}
                        {/* <Button variant="text" className="p-0 gap-1.5 h-auto leading-none text-c46 dark:text-c46" startIcon={<HelpOutlineIcon />} onClick={() => false} children={t('buttons.more')} /> */}
                    </div>
                </>
            )}
        </HintContentWrap>
    );
});
